/* script */

import BaseResource from '../resources/base'

const __vue_script__ = {
    name: 'InputSelectRelatedResource',
    props: {
        resource: {
            required: true,
            type: BaseResource,
        },
        field: {
            required: true,
            type: Object,
        },
        keyname: {
            type: String,
            required: true,
        },
        format: {
            type: Function,
            required: false,
            default(resource) {
                return String(resource)
            },
        },
        value: {
            required: false,
            default: null,
        },
        query: {
            type: [Object, Function, Promise],
            required: false,
            default() {
                return {}
            },
        },
        resourceForm: {
            type: Object,
            required: false,
        },
        size: {
            type: String,
            default: 'md',
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        pageSize: {
            type: [String, Number],
            default: 10,
        },
        noIcons: {
            type: Boolean,
            default: false,
        },
        debounce: {
            type: Number,
            default: 500,
        },
        blurTimeout: {
            type: Number,
            default: 300,
        },
        placeholder: {
            type: String,
        },
    },
    computed: {
        carryOverProps() {
            return {
                size: this.size,
                readonly: this.readonly,
                placeholder: this.placeholder,
            }
        },
        resultIds() {
            return this.resourceResults.map((resource) => resource.id)
        },
        dropdownClasses() {
            let classes = ['dropdown-menu']
            if (this.showResults && this.resourceResults.length > 0) {
                classes.push('show')
            }

            return classes
        },
        parentResourceForm() {
            try {
                if (this.$parent.$parent.$vnode.tag.match(/ResourceEditForm/gi)) {
                    return this.$parent.$parent
                }
            } catch (e) {
                if (!(e instanceof TypeError)) {
                    throw e
                }
            }
        },
    },
    data() {
        return {
            loading: true,
            searching: false,
            selected: null,
            manager: null,
            focused: false,
            initialValue: null,
            initialResource: null,
            showResults: false,
            hasChanged: false,
            resourceResults: [],
            searchString: '',
        }
    },
    watch: {
        searchString(value) {
            if (this.loading) {
                return false
            }

            this.hasChanged = true

            if (value !== this.initialValue) {
                this.doSearch(value)
            }
        },
        selected(valueOrResource) {
            let emitVal = valueOrResource ? valueOrResource.id : valueOrResource
            this.$emit('input', emitVal, valueOrResource)
        },
        loading(value) {
            if (!value && this.focused) {
                // this.focused has been set to true before searchInput renders, this is
                // usually due to the dom's focus() being called on this component whose
                // root div isn't an input. So when this input (searchInput) is rendered,
                // focus it then
                this.$nextTick(() => {
                    this.$refs.searchInput.focus()
                })
            }
        },
    },
    methods: {
        /**
         * Search with a keyword and return a list of resources
         */
        async search(keyword) {
            this.searching = true
            let query = await this.getQueryParams()
            let result = await this.manager.to.search(this.searchString, query)
            this.searching = false
            return result.resources
        },
        /**
         * Perform the search with the state of this
         */
        async doSearch(value) {
            if (!this.focused) {
                return false
            }

            this.resourceResults = await this.search(value)

            if (this.resourceResults.length > 0 && this.searchString !== '') {
                // By default, select the first result
                this.selected = this.resourceResults[0]
            }

            if (!this.focused) {
                // At this point, the search() method above has returned results, but the
                // user has already tabbed away from the cell. Since the first resource has
                // already been selected, update the searchInput value as well
                this.searchString = await this.getQueryValueFromResource(this.selected)
            }
        },
        /**
         * Get searchString value (the relatedDisplayKey) from the related resource
         */
        async getQueryValueFromResource(resource) {
            if (!resource) {
                return ''
            }

            return await resource.resolveAttribute(this.field.relatedDisplayKey)
        },
        /**
         * Get query params to pass to Resource.search() method
         */
        async getQueryParams() {
            let baseQueryParams = { page_size: this.pageSize }
            // Allow query params to be generated preflight
            if ('function' === typeof this.query) {
                let query = await this.query(this.resource, this)
                return Object.assign(baseQueryParams, query)
            } else if (this.query instanceof Promise) {
                let query = await this.query
                return Object.assign(baseQueryParams, query)
            }

            return Object.assign(baseQueryParams, this.query)
        },
        /**
         * Rendered value handler
         */
        formatDropdownItem(resource) {
            return this.format(resource)
        },
        focus() {
            if (!this.$refs.searchInput) {
                this.focused = true
            } else {
                this.$refs.searchInput.focus()
            }
        },
        selectNext() {
            if (!this.resourceResults) {
                return undefined
            }

            if (!this.selected) {
                this.selected = this.resourceResults[0]
                return
            }

            let nextIndex = this.resultIds.indexOf(this.selected.id) + 1
            if (nextIndex >= this.resourceResults.length) {
                this.selected = null
            } else {
                this.selected = this.resourceResults[nextIndex]
            }
        },
        selectPrev() {
            if (!this.resourceResults) {
                return undefined
            }

            if (!this.selected) {
                this.selected = this.resourceResults[this.resourceResults.length - 1]
                return
            }

            let prevIndex = this.resultIds.indexOf(this.selected.id) - 1
            if (prevIndex < 0) {
                this.selected = null
            } else {
                this.selected = this.resourceResults[prevIndex]
            }
        },
        async reset() {
            this.searchString = await this.getQueryValueFromResource(this.initialResource)
            this.selected = this.initialResource
            this.$nextTick(() => (this.hasChanged = false))
        },
        async onKeyDown(e) {
            if (this.resourceResults.length > 0) {
                switch (e.key.toLowerCase()) {
                    case 'arrowdown':
                        this.selectNext()
                        break
                    case 'arrowup':
                        this.selectPrev()
                        break
                    case 'enter':
                        this.$refs.searchInput.blur()
                        await this.selectResource(this.selected)
                }
            }

            this.$emit('keydown', e)
        },
        onFocus() {
            this.focused = true
            this.showResults = true
            this.$refs.searchInput.focus()
            this.$refs.searchInput.select()
        },
        onBlur() {
            this.focused = false
            setTimeout(this.onBlurDeferred, this.blurTimeout)
        },
        async onBlurDeferred() {
            this.showResults = false

            if (!this.hasChanged) {
                await this.reset()
            } else if (this.searchString === '') {
                this.selected = null
            } else {
                this.searchString = await this.getQueryValueFromResource(this.selected)
            }
        },
        async selectResource(resource) {
            this.selected = resource
            this.searchString = await this.getQueryValueFromResource(this.selected)
        },
    },
    async beforeMount() {
        this.loading = true
        this.manager = this.resource.managers[this.keyname]

        if (!this.manager) {
            throw new Error(`Resource manager is required, did you define a relationship to "${this.keyname}"?`)
        }

        this.initialValue = this.value
        await this.manager.resolve()
        this.initialResource = this.manager.resources[0]
        await this.reset()
        this.loading = false
    },
}

/* template */
var __vue_render__ = function() {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _c("div", { staticClass: "resource-select-related" }, [
    !_vm.loading
      ? _c(
          "div",
          [
            _vm._t("icons", [
              !_vm.noIcons
                ? _c(
                    "div",
                    { staticClass: "icons" },
                    [
                      _vm.searching
                        ? _c("b-icon", {
                            attrs: {
                              icon: "hourglass-split",
                              title: "Loading...",
                              animation: "spin-pulse"
                            }
                          })
                        : _vm._e(),
                      _vm._v(" "),
                      _vm.hasChanged
                        ? _c("b-icon", {
                            staticClass: "help link",
                            attrs: {
                              icon: "trash",
                              title: "Reset to Default Value"
                            },
                            on: { click: _vm.reset }
                          })
                        : _vm._e(),
                      _vm._v(" "),
                      !!_vm.selected
                        ? _c("b-icon", {
                            staticClass: "help",
                            attrs: {
                              icon: "check2",
                              title: "A value has been selected"
                            }
                          })
                        : _vm._e(),
                      _vm._v(" "),
                      _c("b-icon", {
                        staticClass: "help",
                        attrs: {
                          icon: "arrow-left-right",
                          title: "This field can do lookups"
                        }
                      })
                    ],
                    1
                  )
                : _vm._e()
            ]),
            _vm._v(" "),
            _c(
              "b-form-input",
              _vm._b(
                {
                  ref: "searchInput",
                  attrs: { autocomplete: "off", debounce: _vm.debounce },
                  on: {
                    focus: _vm.onFocus,
                    blur: _vm.onBlur,
                    keydown: _vm.onKeyDown
                  },
                  model: {
                    value: _vm.searchString,
                    callback: function($$v) {
                      _vm.searchString = $$v
                    },
                    expression: "searchString"
                  }
                },
                "b-form-input",
                _vm.carryOverProps,
                false
              )
            ),
            _vm._v(" "),
            _c("div", { staticClass: "resource-select-related-dropdown" }, [
              _c(
                "ul",
                { class: _vm.dropdownClasses, attrs: { tabindex: "-1" } },
                _vm._l(_vm.resourceResults, function(resource, i) {
                  return _c("li", { key: i }, [
                    _c(
                      "a",
                      {
                        class:
                          _vm.selected && _vm.selected.id === resource.id
                            ? "dropdown-item active"
                            : "dropdown-item",
                        attrs: { href: "javascript:{}", tabindex: "-1" },
                        on: {
                          click: function($event) {
                            return _vm.selectResource(resource)
                          }
                        }
                      },
                      [_vm._v(_vm._s(_vm.formatDropdownItem(resource)))]
                    )
                  ])
                }),
                0
              )
            ])
          ],
          2
        )
      : _c(
          "div",
          [
            _c(
              "b-form-input",
              _vm._b(
                { attrs: { disabled: "", value: "Loading..." } },
                "b-form-input",
                _vm.carryOverProps,
                false
              )
            )
          ],
          1
        )
  ])
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = function (inject) {
    if (!inject) return
    inject("data-v-71d006b4_0", { source: ".resource-select-related[data-v-71d006b4] {\n  position: relative;\n}\n.resource-select-related .icons[data-v-71d006b4] {\n  position: absolute;\n  width: 100%;\n  text-align: right;\n  margin-top: -26px;\n  font-size: 0.8em;\n}\n.resource-select-related .icons .link[data-v-71d006b4] {\n  cursor: pointer;\n}\n.resource-select-related .icons .help[data-v-71d006b4] {\n  cursor: help;\n}\n.resource-select-related .resource-select-related-dropdown[data-v-71d006b4] {\n  position: relative;\n  width: 100%;\n}\n.resource-select-related .resource-select-related-dropdown .dropdown-menu[data-v-71d006b4] {\n  width: 100%;\n  min-width: 240px;\n  overflow: hidden;\n  position: absolute;\n  top: 0px;\n  left: 0px;\n  will-change: transform;\n}\n\n", map: "AAyVA;EACA,kBAAA;AAxVA;AA0VA;EACA,kBAAA;EACA,WAAA;EACA,iBAAA;EACA,iBAAA;EACA,gBAAA;AAxVA;AA0VA;EACA,eAAA;AAxVA;AA2VA;EACA,YAAA;AAzVA;AA6VA;EACA,kBAAA;EACA,WAAA;AA3VA;AA6VA;EACA,WAAA;EACA,gBAAA;EACA,gBAAA;EACA,kBAAA;EACA,QAAA;EACA,SAAA;EACA,sBAAA;AA3VA;;AAEA,wDAAwD", media: undefined })

  }
  /* scoped */
  const __vue_scope_id__ = "data-v-71d006b4"
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = undefined
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "/src/components/InputSelectRelatedResource.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (true) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__() {
    const styles = __vue_create_injector__.styles || (__vue_create_injector__.styles = {})
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase())

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined })

      if (!style.ids.includes(id)) {
        let code = css.source
        let index = style.ids.length

        style.ids.push(id)

        if (false && css.map) {
          // https://developer.chrome.com/devtools/docs/javascript-debugging
          // this makes source maps inside style tags work properly in Chrome
          code += '\n/*# sourceURL=' + css.map.sources[0] + ' */'
          // http://stackoverflow.com/a/26603875
          code +=
            '\n/*# sourceMappingURL=data:application/json;base64,' +
            btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
            ' */'
        }

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']')
        }

        if (!style.element) {
          const head = document.head || document.getElementsByTagName('head')[0]
          const el = style.element = document.createElement('style')
          el.type = 'text/css'

          if (css.media) el.setAttribute('media', css.media)
          if (isOldIE) {
            el.setAttribute('data-group', group)
            el.setAttribute('data-next-index', '0')
          }

          head.appendChild(el)
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'))
          style.element.setAttribute('data-next-index', index + 1)
        }

        if (style.element.styleSheet) {
          style.parts.push(code)
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n')
        } else {
          const textNode = document.createTextNode(code)
          const nodes = style.element.childNodes
          if (nodes[index]) style.element.removeChild(nodes[index])
          if (nodes.length) style.element.insertBefore(textNode, nodes[index])
          else style.element.appendChild(textNode)
        }
      }
    }
  }
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    __vue_create_injector__,
    undefined,
    undefined
  )

  export default __vue_component__


//////////////////
// WEBPACK FOOTER
// ./~/@sigmacloud/sigma-client/dist/components/InputSelectRelatedResource.js
// module id = SDyi
// module chunks = 0